// ChangePasswordModal.jsx
import React, { useState } from 'react';
import { X, Lock, Eye, EyeOff } from 'lucide-react';

const ChangePasswordModal = ({ isOpen, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.currentPassword) newErrors.currentPassword = 'Current password is required';
    if (!formData.newPassword) newErrors.newPassword = 'New password is required';
    if (formData.newPassword.length < 6) newErrors.newPassword = 'Password must be at least 6 characters';
    if (formData.newPassword !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-md transition-all duration-300 bg-white border border-gray-200 shadow-2xl rounded-2xl dark:bg-gray-900 dark:border-gray-800">
        
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100/20 dark:bg-blue-500/20">
              <Lock className="w-5 h-5 text-blue-500 dark:text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
              Change Password
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {['current', 'new', 'confirm'].map((field) => {
            const isCurrent = field === 'current';
            const label = isCurrent ? 'Current Password' : field === 'new' ? 'New Password' : 'Confirm Password';
            const value = isCurrent ? formData.currentPassword : field === 'new' ? formData.newPassword : formData.confirmPassword;
            const errorKey = isCurrent ? 'currentPassword' : field === 'new' ? 'newPassword' : 'confirmPassword';
            
            return (
              <div key={field}>
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {label}
                </label>
                <div className="relative">
                  <input
                    type={showPasswords[field] ? 'text' : 'password'}
                    value={value}
                    onChange={(e) => {
                      setFormData({ 
                        ...formData, 
                        [errorKey]: e.target.value 
                      });
                      if (errors[errorKey]) {
                        setErrors({ ...errors, [errorKey]: '' });
                      }
                    }}
                    className={`w-full px-4 py-2 rounded-lg border transition-all duration-200 pr-10
                      bg-white dark:bg-gray-800 
                      border-gray-300 dark:border-gray-700 
                      text-gray-900 dark:text-white 
                      focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20
                      ${errors[errorKey] && 'border-red-500 dark:border-red-500'}`}
                    placeholder={`Enter ${label.toLowerCase()}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] })}
                    className="absolute -translate-y-1/2 right-3 top-1/2"
                  >
                    {showPasswords[field] ? (
                      <EyeOff className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    )}
                  </button>
                </div>
                {errors[errorKey] && (
                  <p className="mt-1 text-sm text-red-500 dark:text-red-400">{errors[errorKey]}</p>
                )}
              </div>
            );
          })}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 font-medium text-gray-700 transition-all duration-200 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 font-medium text-white transition-all duration-200 rounded-lg bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600 disabled:opacity-50"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;